import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { STATUS } from '../models/status';
import { HttpService } from './http.service';
import { LoggerService } from './logger.service';
import { buildUserBalanceConfigFromData } from '../utils/merge-balance';
import { AppService } from './app.service';
import { AuthService } from './auth.service';
import { Roles } from '../models/roles';

const USER_CURRENCY_RESTRICTIONS: Record<number, string[]> = {
  36: ['EUR'],
};

@Injectable()
export class UserService {
  users: any[] = [];
  clients: any[] = [];
  providers: any[] = [];
  platformServices: any[] = [];
  platformServicesLoaded = false;
  platformServiceCategories: any[] = [];
  platformServiceCategoriesLoaded = false;
  currentUserPlatformServices: any[] = [];
  currentUserPlatformServicesLoaded = false;
  private currentUserPlatformServicesSource = new BehaviorSubject<any[]>([]);
  currentUserPlatformServices$ =
    this.currentUserPlatformServicesSource.asObservable();

  balances: any = null;
  user_balance_config: any;

  constructor(
    private http: HttpService,
    private logger: LoggerService,
    private appService: AppService,
    private authService: AuthService,
  ) {}

  getUsers(pagination: { page?: number | string; page_size?: number | string } = {}) {
    let params = new HttpParams();
    if (pagination.page !== undefined && pagination.page !== null) {
      params = params.set('page', String(pagination.page));
    }
    if (pagination.page_size !== undefined && pagination.page_size !== null) {
      params = params.set('page_size', String(pagination.page_size));
    }

    return new Promise((resolve, reject) => {
      this.http.get('user/providers-clients', params).subscribe(
        (resp: any) => {
          this.providers = resp.data.p || [];
          this.clients = resp.data.c || [];

          // Usar la lista completa cuando el backend la envía; evita duplicados
          // cuando un rol pertenece a más de una agrupación visual.
          this.users = resp.data.u || [...this.providers, ...this.clients];

          // Ordenar alfabéticamente por username
          this.providers.sort((a: any, b: any) =>
            (a.username || '').localeCompare(b.username || '', undefined, {
              sensitivity: 'base', // ignora mayúsculas/minúsculas
            }),
          );
          this.clients.sort((a: any, b: any) =>
            (a.username || '').localeCompare(b.username || '', undefined, {
              sensitivity: 'base', // ignora mayúsculas/minúsculas
            }),
          );
          this.users.sort((a: any, b: any) =>
            (a.username || '').localeCompare(b.username || '', undefined, {
              sensitivity: 'base', // ignora mayúsculas/minúsculas
            }),
          );

          this.logger.log('providers:', this.providers);
          this.logger.log('clients:', this.clients);
          this.logger.log('users:', this.users);

          resolve(resp);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  updateUser(body: any, id: any) {
    return this.http.patch(`user/` + id, body);
  }

  /**
   * Prioridades por moneda/servicio (tabla client_service_provider_priority).
   * Admin only. Docs: transferswap_mvp/docs/CLIENT_SERVICE_PROVIDER_PRIORITY.md
   */
  getClientProviderPriorities(userId: number | string) {
    return this.http.get(`user/${userId}/provider-priorities`);
  }

  putClientProviderPriorities(
    userId: number | string,
    priorities: Record<string, Record<string, number[]>>,
  ) {
    return this.http.put(`user/${userId}/provider-priorities`, { priorities });
  }

  checkUserAvailability(params: {
    email?: string;
    document_number?: string;
    company_registration_number?: string;
    username?: string;
  }) {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      const trimmed = String(value || '').trim();
      if (trimmed) {
        httpParams = httpParams.set(key, trimmed);
      }
    });
    return this.http.get('user/availability', httpParams);
  }

  createUser(body: {
    user_type?: 'user' | 'subuser';
    entity_type?: 'natural' | 'business';
    username: string;
    password?: string;
    role_id: number;
    configuration_id: number | string;
    status_compliance?: string;
    api_key?: string | null;
    client_partner_id?: number | null;
    end_user_token_exp_days?: number | null;
    white_label?: boolean;
    reliance_enabled?: boolean;
    email: string;
    first_name?: string;
    middle_name?: string;
    surname?: string;
    second_surname?: string;
    company_name?: string;
    brand_name?: string;
    company_registration_number?: string;
    document_type?: 'DNI' | 'PASSPORT' | 'PHOTO_LICENSE' | '';
    document_number?: string;
    address_line_1: string;
    address_line_2?: string;
    city: string;
    postal_code: string;
    state: string;
    country: string;
  }) {
    return this.http.post('user', body);
  }

  createRelianceUser(body: {
    user_id: number;
    email: string;
    first_name: string;
    middle_name?: string;
    surname: string;
    second_surname: string;
    document_type: string;
    document_number: string;
    address: string;
    phone_number: string;
    external_reference: string;
  }) {
    return this.http.post('reliance-user', body);
  }

  getRelianceUser(userId: number | string) {
    return this.http.get(`reliance-user/${userId}`);
  }

  createConfiguration(body: any) {
    return this.http.post('configuration', body);
  }

  getBaseConfiguration() {
    return this.http.get('configuration/base');
  }

  getBaseProviderPriorities() {
    return this.http.get('provider-priorities/base');
  }

  putBaseProviderPriorities(priorities: Record<string, Record<string, number[]>>) {
    return this.http.put('provider-priorities/base', { priorities });
  }

  resetUserPassword(userId: any) {
    return this.http.post(`user/${userId}/password/reset`, {});
  }

  getPlatformServices() {
    return this.http.get('platform-services');
  }

  loadPlatformServices(force = false): Promise<any[]> {
    if (this.platformServicesLoaded && !force) {
      return Promise.resolve(this.platformServices);
    }

    return new Promise((resolve, reject) => {
      this.getPlatformServices().subscribe(
        (resp: any) => {
          this.platformServices = resp?.data || [];
          this.platformServicesLoaded = true;
          resolve(this.platformServices);
        },
        (error) => reject(error),
      );
    });
  }

  getPlatformServiceCategories() {
    return this.http.get('platform-service-categories');
  }

  loadPlatformServiceCategories(force = false): Promise<any[]> {
    if (this.platformServiceCategoriesLoaded && !force) {
      return Promise.resolve(this.platformServiceCategories);
    }

    return new Promise((resolve, reject) => {
      this.getPlatformServiceCategories().subscribe(
        (resp: any) => {
          this.platformServiceCategories = resp?.data || [];
          this.platformServiceCategoriesLoaded = true;
          resolve(this.platformServiceCategories);
        },
        (error) => reject(error),
      );
    });
  }

  getUserPlatformServices(userId: any = null) {
    let params = new HttpParams();
    if (userId) {
      params = params.set('user_id', userId.toString());
    }
    return this.http.get('user-platform-services', params);
  }

  loadCurrentUserPlatformServices(force = false): Promise<any[]> {
    if (this.authService.hasRole(Roles.Admin)) {
      this.setCurrentUserPlatformServices([]);
      this.currentUserPlatformServicesLoaded = true;
      return Promise.resolve(this.currentUserPlatformServices);
    }

    if (this.currentUserPlatformServicesLoaded && !force) {
      return Promise.resolve(this.currentUserPlatformServices);
    }

    if (!this.authService.user?.user_id) {
      this.setCurrentUserPlatformServices([]);
      this.currentUserPlatformServicesLoaded = true;
      return Promise.resolve(this.currentUserPlatformServices);
    }

    return new Promise((resolve, reject) => {
      this.getUserPlatformServices().subscribe(
        (resp: any) => {
          this.setCurrentUserPlatformServices(resp?.data || []);
          this.currentUserPlatformServicesLoaded = true;
          resolve(this.currentUserPlatformServices);
        },
        (error) => reject(error),
      );
    });
  }

  hasPlatformService(required: string | string[] | null | undefined): boolean {
    if (this.authService.hasRole(Roles.Admin)) {
      return true;
    }

    const requiredCodes = this.normalizePlatformServiceRequirements(required);
    if (!requiredCodes.length) {
      return false;
    }

    return requiredCodes.some((requiredCode) =>
      this.currentUserPlatformServices.some((permission) =>
        this.platformServiceMatches(permission, requiredCode),
      ),
    );
  }

  hasAllPlatformServices(required: string | string[] | null | undefined): boolean {
    if (this.authService.hasRole(Roles.Admin)) {
      return true;
    }

    const requiredCodes = this.normalizePlatformServiceRequirements(required);
    if (!requiredCodes.length) {
      return false;
    }

    return requiredCodes.every((requiredCode) =>
      this.currentUserPlatformServices.some((permission) =>
        this.platformServiceMatches(permission, requiredCode),
      ),
    );
  }

  canCreatePlatformUser(): boolean {
    return (
      this.authService.hasRole(Roles.Admin) ||
      this.hasPlatformService('user:create')
    );
  }

  canCreatePlatformSubuser(): boolean {
    return (
      this.authService.hasRole(Roles.Admin) ||
      this.hasPlatformService('subuser:create')
    );
  }

  canCreateUserOrSubuser(): boolean {
    return this.canCreatePlatformUser() || this.canCreatePlatformSubuser();
  }

  buildPlatformServiceCode(category: string, currency: any = null): string {
    const cleanCategory = (category || '').toString().trim().toLowerCase();
    const cleanCurrency = (currency ?? '').toString().trim().toLowerCase();

    if (!cleanCategory) {
      return '';
    }

    return cleanCurrency ? `${cleanCategory}:${cleanCurrency}` : `${cleanCategory}:*`;
  }

  getPlatformServiceAllowedCurrencies(category: string): any[] {
    const currencies = this.user_balance_config?.currencies ?? [];

    return currencies.filter((currency: any) =>
      this.isPlatformServiceCurrencyAllowed(category, currency),
    );
  }

  isPlatformServiceCurrencyAllowed(category: string, currency: any): boolean {
    const currencyId = this.extractPlatformServiceCurrencyId(currency);

    if (!currencyId) {
      return false;
    }

    return this.hasPlatformService(
      this.buildPlatformServiceCode(category, currencyId),
    );
  }

  createUserPlatformService(body: any) {
    return this.http.post('user-platform-services', body);
  }

  createUserPlatformServices(body: any) {
    return this.http.post('user-platform-services', body);
  }

  updateUserPlatformService(id: any, body: any) {
    return this.http.patch(`user-platform-services/${id}`, body);
  }

  deleteUserPlatformService(id: any, includeWhiteLabel = false) {
    const params = includeWhiteLabel
      ? new HttpParams().set('include_white_label', 'true')
      : undefined;
    return this.http.delete(`user-platform-services/${id}`, params);
  }

  createPlatformService(body: any) {
    return this.http.post('platform-services', body);
  }

  updatePlatformService(id: any, body: any) {
    return this.http.patch(`platform-services/${id}`, body);
  }

  updateMyPlatformServicePermission(id: any, body: { active: boolean }) {
    return this.http.patch(`platform-services/${id}/my-permission`, body);
  }

  deletePlatformService(id: any) {
    return this.http.delete(`platform-services/${id}`);
  }

  propagatePlatformService(id: any) {
    return this.http.post(`platform-services/${id}/propagate`, {});
  }

  removePlatformServiceFromUsers(
    id: any,
    body: { include_white_label?: boolean } = {},
  ) {
    return this.http.post(`platform-services/${id}/remove-from-users`, body);
  }

  renewEndUserToken(
    userId: number | string,
    body: { end_user_token_exp_days?: number } = {},
  ) {
    return this.http.post(`user/${userId}/end-user-token/renew`, body);
  }

  updateMyUserInfo(body: any) {
    return new Promise((resolve, reject) => {
      this.http.patch('user/profile/user-info', body).subscribe(
        (resp: any) => resolve(resp),
        (error) => reject(error),
      );
    });
  }

  downloadVesBalanceReport(date: string, userId: any = null) {
    let params = new HttpParams().set('date', date);

    if (userId) {
      params = params.set('user_id', userId.toString());
    }

    return this.http.getBlob('summary/ves-balance-report', params);
  }

  getUsernameById(id: any) {
    let user = this.clients.find((user) => user.user_id === Number(id));

    if (!user) {
      user = this.providers.find((user) => user.user_id === Number(id));
    }

    if (id === 1) return 'TransferSwap';

    return user?.username;
  }

  setUserBalance(user_id: any = null) {
    return new Promise((resolve, reject) => {
      let params = new HttpParams();
      const effectiveUserId = user_id ?? this.authService.user?.user_id ?? null;

      if (user_id) {
        params = params.set('user_id', user_id.toString());
      }

      this.http.get(`balance`, params).subscribe(
        (resp: any) => {
          this.balances = resp.data;

          let balances = buildUserBalanceConfigFromData(
            this.appService.config,
            this.balances,
          );
          balances = this.applyCurrencyRestrictions(balances, effectiveUserId);

          if (!user_id) {
            this.user_balance_config = balances;
            this.logger.log('Config: ', this.user_balance_config);
          }

          resolve(balances);
        },
        (error) => {
          console.error('Error retrieving balance:', error);
          reject(error);
        },
      );
    });
  }

  private applyCurrencyRestrictions(balanceConfig: any, userId: any): any {
    const allowedCurrencies = USER_CURRENCY_RESTRICTIONS[Number(userId)];

    if (!allowedCurrencies?.length) {
      return balanceConfig;
    }

    const allowed = new Set(allowedCurrencies.map((currency) => this.norm(currency)));
    const currencies = (balanceConfig?.currencies ?? []).filter((currency: any) =>
      this.currencyMatchesRestriction(currency, allowed),
    );
    const transactionTypes = (balanceConfig?.transaction_types ?? []).filter(
      (type: any) => this.transactionTypeMatchesRestriction(type, allowed),
    );

    return {
      ...balanceConfig,
      currencies,
      transaction_types: transactionTypes,
    };
  }

  private currencyMatchesRestriction(currency: any, allowed: Set<string>): boolean {
    return [
      currency?.currency,
      currency?.id,
      currency?.shortname,
      currency?.name,
    ].some((value) => allowed.has(this.norm(value)));
  }

  private transactionTypeMatchesRestriction(
    txType: any,
    allowed: Set<string>,
  ): boolean {
    const currencies = this.normalizeCurrencyCodes(txType?.currency);

    if (!currencies.length) {
      return true;
    }

    return currencies.some(
      (currency) =>
        allowed.has(currency) ||
        currency === 'ALL' ||
        currency === '*' ||
        currency === 'ANY',
    );
  }

  private normalizeCurrencyCodes(raw: any): string[] {
    const result = new Set<string>();
    const visited = new Set<any>();

    const visit = (value: any) => {
      if (value == null) {
        return;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
          return;
        }

        if (
          (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
          (trimmed.startsWith('{') && trimmed.endsWith('}'))
        ) {
          try {
            visit(JSON.parse(trimmed));
            return;
          } catch {
            // Treat non-JSON strings as a single currency code.
          }
        }

        trimmed
          .split(',')
          .map((part) => this.norm(part))
          .filter(Boolean)
          .forEach((part) => result.add(part));
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(visit);
        return;
      }

      if (typeof value === 'object') {
        if (visited.has(value)) {
          return;
        }
        visited.add(value);

        ['currency', 'currencies', 'id', 'code'].forEach((key) =>
          visit(value[key]),
        );
      }
    };

    visit(raw);
    return Array.from(result);
  }

  private norm(value: any): string {
    return (value ?? '').toString().trim().toUpperCase();
  }

  private setCurrentUserPlatformServices(permissions: any[]) {
    this.currentUserPlatformServices = permissions;
    this.currentUserPlatformServicesSource.next(permissions);
  }

  private normalizePlatformServiceRequirements(
    required: string | string[] | null | undefined,
  ): string[] {
    const list = Array.isArray(required) ? required : [required];

    return list
      .map((value) => (value ?? '').toString().trim().toLowerCase())
      .filter(Boolean);
  }

  private platformServiceMatches(permission: any, requiredCode: string): boolean {
    if (!this.isActivePlatformServicePermission(permission)) {
      return false;
    }

    const assignedCode = this.getPlatformServicePermissionCode(permission);
    if (!assignedCode) {
      return false;
    }

    if (requiredCode.endsWith(':*')) {
      return assignedCode.startsWith(requiredCode.slice(0, -1));
    }

    if (!requiredCode.includes(':')) {
      return assignedCode === requiredCode || assignedCode.startsWith(`${requiredCode}:`);
    }

    return assignedCode === requiredCode;
  }

  private getPlatformServicePermissionCode(permission: any): string {
    return (
      permission?.platform_service?.code ??
      permission?.platform_service_code ??
      permission?.service_code ??
      permission?.code ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();
  }

  private isActivePlatformServicePermission(permission: any): boolean {
    if (permission?.effective_active !== undefined && permission?.effective_active !== null) {
      return this.isTruthyFlag(permission.effective_active, false);
    }

    const permissionActive = permission?.active;
    const serviceActive = permission?.platform_service?.active;

    return (
      this.isTruthyFlag(permissionActive, true) &&
      this.isTruthyFlag(serviceActive, true)
    );
  }

  private isTruthyFlag(value: any, defaultValue = false): boolean {
    if (value === undefined || value === null || value === '') {
      return defaultValue;
    }

    if (typeof value === 'string') {
      return ['1', 'true', 'activo', 'active', 'yes'].includes(
        value.trim().toLowerCase(),
      );
    }

    return Boolean(value);
  }

  private extractPlatformServiceCurrencyId(currency: any): string {
    if (!currency) {
      return '';
    }

    if (typeof currency === 'string') {
      return currency.trim().toLowerCase();
    }

    return (currency?.id ?? currency?.currency ?? currency?.code ?? '')
      .toString()
      .trim()
      .toLowerCase();
  }
}
